/**
 * FNV-1a 32-bit over a canonical JSON encoding. Deterministic across engines:
 * pure integer arithmetic, keys emitted in sorted order.
 *
 * Used as the replay fingerprint so CI can prove a refactor did not change a
 * single battle outcome.
 */

export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return "[" + value.map(canonicalJson).join(",") + "]";
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts: string[] = [];
  for (const k of keys) {
    if (obj[k] === undefined) continue;
    parts.push(JSON.stringify(k) + ":" + canonicalJson(obj[k]));
  }
  return "{" + parts.join(",") + "}";
}

export function fnv1a32(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // h *= 16777619, kept in 32-bit unsigned space without floats
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/** Hex fingerprint of any JSON-serialisable value. */
export function hashOf(value: unknown): string {
  return fnv1a32(canonicalJson(value)).toString(16).padStart(8, "0");
}
